import React, { memo, useEffect, useMemo } from 'react';
import _get from 'lodash/get';
import { useStatus, useData, combineStatuses, useClearFetch } from 'resift';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router';
import makeUpdateEntityType from 'fetches/makeUpdateEntityType';
import makeGetFieldsFetch from 'fetches/makeGetFieldsFetch';
import makeGetEntityTypeStatus from 'fetches/makeGetEntityTypeStatus';
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';
import makeUpdateCategoryFieldFetch from 'store/attribute/actions/updateCategoryFieldFetch';
import makeUpdateLayoutFetch from 'pages/ProfileLayout/fetches/updateLayoutFetch';
import makeGetEntityTypeConfiguration from 'fetches/makeGetEntityTypeConfiguration';
import useEntityTypes from 'helpers/useEntityTypes';
import showSnackbar from 'helpers/showSnackbar';
import * as Types from 'pages/Pages/typings';
import { LayoutObject } from 'pages/AttributesEdit/AttributesEdit';

import PageTypes from './PageTypes';
import Loader from 'components/Loader';

function Container() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const entityTypes = useEntityTypes();
  const clearFetch = useClearFetch();

  let { entityTypeKey } = useParams<{ entityTypeKey: string }>();
  const entityTypeData = useMemo(() => _get(entityTypes, entityTypeKey), [
    entityTypes,
    entityTypeKey,
  ]);

  // entity types
  const entityTypesFetch = makeEntityTypesFetch();
  const entityTypeTypeKey = location.pathname.split('/')[3];
  const getEntityTypeConfig = makeGetEntityTypeConfiguration(entityTypeTypeKey);
  const getEntityTypeConfigStatus = useStatus(getEntityTypeConfig);
  const entityTypeConfig = useData(getEntityTypeConfig);

  // config fields
  const fieldsFetch = makeGetFieldsFetch();
  const fieldsStatus = useStatus(fieldsFetch);

  // entities list upload status:
  const getEntityTypeUploadStatus = makeGetEntityTypeStatus(entityTypeData.id || entityTypeKey);
  const entityTypeUploadData = useData(getEntityTypeUploadStatus);

  // update entity type:
  const updateEntityType = makeUpdateEntityType(entityTypeKey);
  const updateEntityTypeStatus = useStatus(updateEntityType);

  const handleUpdateEntityType = async (data: Types.UpdateEntity) => {
    await dispatch(updateEntityType(data));
    dispatch(entityTypesFetch());
  };

  // Field Update (valueReadOnly)
  const updateField = makeUpdateCategoryFieldFetch();
  const updateFieldStatus = useStatus(updateField);

  const handleUpdateField = async (fieldId: string, editedAttribute: Types.UpdateField) => {
    await dispatch(updateField(fieldId, editedAttribute));
    dispatch(entityTypesFetch());
  };

  // Layout Update (for person attributes filters and highlight-by)
  const updateLayout = makeUpdateLayoutFetch();
  const updateLayoutStatus = useStatus(updateLayout, { isolatedStatus: true });

  const handleUpdateLayout = async (payload: LayoutObject[]) => {
    await dispatch(updateLayout(payload));
    dispatch(entityTypesFetch());
    dispatch(getEntityTypeConfig());
  };

  const handleGetMigrationStatus = () => {
    if (entityTypeData) {
      dispatch(getEntityTypeUploadStatus());
    }
  };

  const combinedUpdateStatus = combineStatuses(
    updateLayoutStatus || 1,
    updateEntityTypeStatus || 1,
    updateFieldStatus || 1,
  );

  useEffect(() => {
    if (entityTypeData) {
      dispatch(fieldsFetch(entityTypeKey));
      dispatch(getEntityTypeUploadStatus());
    }
  }, [entityTypeData, entityTypeKey]);

  useEffect(() => {
    dispatch(getEntityTypeConfig());
  }, []);

  const combinedGetStatus = combineStatuses(fieldsStatus || 1, getEntityTypeConfigStatus || 1);

  useEffect(() => {
    if (!entityTypeData) {
      history.push('/404');
    }

    return () => {
      if (!entityTypeData) {
        clearFetch(fieldsFetch);
        clearFetch(getEntityTypeUploadStatus);
      }
    };
  }, [entityTypeData]);

  showSnackbar({
    status: combinedUpdateStatus,
    variant: 'success',
    message: 'You have successfully updated your page.',
    errorMessage: 'There was an error submitting your changes. Please try again.',
  });

  if (!entityTypeConfig) {
    return null;
  }

  return (
    <Loader status={combinedGetStatus}>
      {() => (
        <PageTypes
          entityType={entityTypeData}
          migrationInProgress={_get(entityTypeUploadData, 'attributeMigrationInProgress', false)}
          status={combinedUpdateStatus}
          entityTypeConfig={entityTypeConfig}
          onReload={handleGetMigrationStatus}
          onUpdateEntityType={handleUpdateEntityType}
          onUpdateField={handleUpdateField}
          onUpdateLayout={handleUpdateLayout}
        />
      )}
    </Loader>
  );
}

export default memo(Container);
